import {
  operativeActivitiesStore,
  getProgress,
  getStepTitlesForActivity,
} from './operativeActivities'

export interface ActionItem {
  id: string
  name: string
  assigneeType: 'consulente' | 'cliente'
  assigneeName: string
  activityProduct: string
  activityId: string
  stepName: string
  stepIndex: number
  stepProgress: number
  deadline: string
  isDone: boolean
  isCurrentStep: boolean
  clientName: string
  responsible: string
  createdAt: string
}

const consulenteActions = [
  'Verifica documentazione',
  'Preparazione report',
  'Sopralluogo sede',
  'Redazione DVR',
  'Invio comunicazione',
  'Aggiornamento fascicolo',
  'Analisi rischi',
  'Compilazione checklist',
  'Formazione operatori',
  'Consegna documentazione',
]

const clienteActions = [
  'Invio documenti richiesti',
  'Firma moduli',
  'Predisposizione accessi',
  'Nomina referente',
  'Aggiornamento organigramma',
  'Raccolta certificati',
]

function generateMockActions(): ActionItem[] {
  const activities = operativeActivitiesStore.getAll()
  const actions: ActionItem[] = []
  let actionIdCounter = 1

  activities.forEach((activity) => {
    const progress = getProgress(activity)
    const stepTitles = getStepTitlesForActivity(activity)
    const totalSteps = stepTitles.length

    // Generate 1-3 actions per step
    for (let stepIndex = 0; stepIndex < totalSteps; stepIndex++) {
      const stepName = stepTitles[stepIndex]
      const isStepCompleted = activity.stepsCompleted[stepIndex]
      const isCurrentStep = stepIndex === progress.currentStep - 1

      // Determine number of actions for this step
      const numActions = Math.floor(Math.random() * 3) + 1

      for (let i = 0; i < numActions; i++) {
        const isConsulente = Math.random() > 0.3
        const assigneeType = isConsulente ? 'consulente' : 'cliente'
        const nameList = isConsulente ? consulenteActions : clienteActions
        const name = nameList[Math.floor(Math.random() * nameList.length)]

        // If step is completed, action is done. If current or future, maybe done, mostly not.
        let isDone = false
        if (isStepCompleted) {
          isDone = true
        } else if (isCurrentStep) {
          isDone = Math.random() > 0.7
        }

        // Deadline: base activity deadline, adjusted by step
        const baseDate = new Date(activity.deadline)
        const daysOffset =
          (stepIndex - totalSteps + 1) * 15 +
          (Math.floor(Math.random() * 10) - 5)
        baseDate.setDate(baseDate.getDate() + daysOffset)

        actions.push({
          id: `act-${actionIdCounter++}`,
          name: `${name} - ${stepName}`,
          assigneeType,
          assigneeName: isConsulente
            ? activity.operator
            : 'Referente Aziendale',
          activityProduct: activity.product,
          activityId: activity.id,
          stepName,
          stepIndex,
          stepProgress: progress.percentage,
          deadline: baseDate.toISOString().split('T')[0],
          isDone,
          isCurrentStep,
          clientName: activity.clientName,
          responsible: activity.operator,
          createdAt: activity.createdAt,
        })
      }
    }
  })

  return actions
}

class ActionsStore {
  private actions: ActionItem[] = generateMockActions()

  getAll(): ActionItem[] {
    return [...this.actions]
  }

  getByClient(clientName: string): ActionItem[] {
    return this.actions.filter((a) => a.clientName === clientName)
  }

  getByResponsible(responsible: string): ActionItem[] {
    return this.actions.filter((a) => a.responsible === responsible)
  }

  toggleDone(id: string): void {
    const action = this.actions.find((a) => a.id === id)
    if (action) {
      action.isDone = !action.isDone
    }
  }

  delete(id: string): void {
    this.actions = this.actions.filter((a) => a.id !== id)
  }
}

export const actionsStore = new ActionsStore()

export function sortActions(actions: ActionItem[]): ActionItem[] {
  return [...actions].sort((a, b) => {
    // 1. Done items at the bottom
    if (a.isDone && !b.isDone) return 1
    if (!a.isDone && b.isDone) return -1

    // Both not done
    if (!a.isDone && !b.isDone) {
      // 2. Current step actions first, then future (pending) steps
      if (a.isCurrentStep && !b.isCurrentStep) return -1
      if (!a.isCurrentStep && b.isCurrentStep) return 1

      // 3. Within same group, sort by deadline ascending (nearest first)
      return new Date(a.deadline).getTime() - new Date(b.deadline).getTime()
    }

    // Both done: sort by deadline descending (most recent first)
    return new Date(b.deadline).getTime() - new Date(a.deadline).getTime()
  })
}
